import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  FiArrowLeft,
  FiChevronDown,
  FiInfo,
  FiSave,
  FiShield,
} from "react-icons/fi";
import {
  AccessLevelName,
  accessLevelClassName,
  accessLevelDisplayName,
  auditTrail,
  permissionCategories,
} from "./PayoutAccessControlData";
import { isPayoutApiMode, payoutApi } from "./api/payoutApiClient";
import { roleFromApiRecord } from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";

function Field({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div>
      <p className="text-xs font-semibold text-[#505E8E]">{label}</p>
      <div className="mt-2 text-sm font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function PayoutRoleDetails() {
  const { roleSlug } = useParams();
  const emptyRole = {
    slug: roleSlug || "",
    name: "",
    type: "",
    accessLevel: "Observer",
    description: "",
    users: 0,
    status: "",
  };
  const roleResource = usePayoutResource(
    emptyRole,
    (signal) =>
      payoutApi
        .roleDetails(roleSlug || "", signal)
        .then((row) => roleFromApiRecord(row, emptyRole)),
    [roleSlug],
  );
  const role = roleResource.data;
  const accessLevel = role.accessLevel as AccessLevelName;

  if (!roleSlug && !isPayoutApiMode()) return <Navigate to="/payouts/roles" replace />;

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <Link
            to="/payouts/roles"
            className="mb-4 inline-flex items-center gap-2 text-sm font-semibold text-[#6687FF]"
          >
            <FiArrowLeft />
            Back to Roles
          </Link>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            {role.name}
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Review inherited permissions and configure role access.
          </p>
        </div>
        <button className="flex h-10 items-center gap-3 rounded-[6px] bg-edviron_black px-5 text-sm font-semibold text-white">
          <FiSave />
          Save Changes
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_360px]">
        <div className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Role Information
            </h2>
            <div className="mt-5 grid gap-5 md:grid-cols-4">
              <Field label="Name" value={role.name} />
              <Field
                label="Access Level"
                value={
                  <span
                    className={
                      "rounded-[5px] px-2 py-1 text-xs " +
                      accessLevelClassName[accessLevel]
                    }
                  >
                    {accessLevelDisplayName[accessLevel]}
                  </span>
                }
              />
              <Field
                label="Status"
                value={
                  <span className="rounded-[5px] bg-[#DCFCE7] px-2 py-1 text-xs text-[#16A34A]">
                    {role.status}
                  </span>
                }
              />
              <Field label="Type" value={role.type} />
            </div>
            <p className="mt-5 text-sm font-medium leading-6 text-[#505E8E]">
              {role.description}
            </p>
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-[18px] font-semibold text-[#1B163B]">
                  Permission Categories
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  Inherited permissions are displayed with their source level.
                </p>
              </div>
              <button className="flex items-center gap-2 text-sm font-semibold text-[#6687FF]">
                Expand all
                <FiChevronDown />
              </button>
            </div>

            <div className="space-y-4">
              {permissionCategories.map((category) => (
                <div
                  key={category.name}
                  className="rounded-[8px] border border-[#E0E5F2] bg-white"
                >
                  <div className="flex items-center justify-between border-b border-[#E0E5F2] px-4 py-3">
                    <h3 className="text-sm font-semibold text-[#1B163B]">
                      {category.name}
                    </h3>
                    <FiChevronDown className="text-[#505E8E]" />
                  </div>
                  <div className="divide-y divide-[#E0E5F2]">
                    {category.permissions.map((permission) => (
                      <div
                        key={permission.label}
                        className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
                      >
                        <label className="flex items-center gap-3 text-sm font-semibold text-[#1B163B]">
                          <input
                            type="checkbox"
                            checked={permission.enabled}
                            readOnly
                            className="h-4 w-4 accent-[#6687FF]"
                          />
                          {permission.label}
                        </label>
                        {permission.inheritedFrom && (
                          <span
                            title="Inherited permissions are automatically included from lower access levels."
                            className="rounded-[5px] bg-[#6687FF1A] px-2 py-1 text-xs font-semibold text-[#6687FF]"
                          >
                            Inherited from {accessLevelDisplayName[permission.inheritedFrom]}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-5">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Inheritance
            </h2>
            <div className="mt-5 space-y-3">
              {(["Observer", "Finance Executive", "Finance Manager", "Administrator"] as const).map(
                (level, index) => (
                  <div key={level} className="flex items-center gap-3">
                    <span
                      className={
                        "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold " +
                        (level === accessLevel
                          ? "bg-edviron_black text-white"
                          : "bg-[#F4F7FE] text-[#505E8E]")
                      }
                    >
                      {index + 1}
                    </span>
                    <span className="text-sm font-semibold text-[#1B163B]">
                      {accessLevelDisplayName[level]}
                    </span>
                  </div>
                ),
              )}
            </div>
            <div className="mt-5 flex gap-3 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#505E8E]">
              <FiInfo className="mt-0.5 shrink-0 text-[#6687FF]" />
              Higher levels automatically receive every permission from lower levels.
            </div>
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h2 className="text-[18px] font-semibold text-[#1B163B]">
              Audit Trail
            </h2>
            <div className="mt-4 space-y-4">
              {auditTrail.map((item) => (
                <div key={item} className="flex gap-3 text-sm">
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#DCFCE7] text-[#16A34A]">
                    <FiShield />
                  </span>
                  <p className="font-medium leading-5 text-[#505E8E]">{item}</p>
                </div>
              ))}
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

export default PayoutRoleDetails;
